import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, CheckCircle2, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Formspree endpoint
      const response = await fetch('https://formspree.io/f/xykblkyz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          message: formData.message
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Message sent successfully! We\'ll be in touch soon.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">
              Get in Touch
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.1]">
              Ready to transform your project delivery?
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed">
              Whether you're looking for strategic advisory support or want to explore 
              how Videra can digitise your portfolio management, we'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-20 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="contact-info"
            >
              {/* Founder Card */}
              <div className="bg-slate-50 p-8 mb-10">
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-6">
                  Get in Touch
                </p>
                <h2 className="text-2xl font-medium text-slate-900">Keystone Strategic Consultants</h2>
                <p className="text-slate-500 mt-1">Project Delivery Excellence</p>
                
                <div className="mt-8 space-y-4">
                  <a 
                    href="mailto:hello@keystoneconsulting.uk"
                    className="flex items-center gap-4 text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid="contact-email-link"
                  >
                    <div className="w-10 h-10 bg-white flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <span>hello@keystoneconsulting.uk</span>
                  </a>
                  
                  <div className="flex items-center gap-4 text-slate-600">
                    <div className="w-10 h-10 bg-white flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <span>+44(0)7703 577261</span>
                  </div>
                  
                  <a 
                    href="https://www.linkedin.com/in/peter-fulford-aa03467/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid="contact-linkedin-link"
                  >
                    <div className="w-10 h-10 bg-white flex items-center justify-center">
                      <Linkedin className="w-5 h-5" />
                    </div>
                    <span>Follow on LinkedIn</span>
                  </a>
                </div>
              </div>

              {/* Office */}
              <div className="mb-10">
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-4">
                  Office
                </p>
                <div className="flex items-start gap-4 text-slate-600">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">Keystone Strategic Consultants</p>
                    <p>United Kingdom</p>
                  </div>
                </div>
              </div>

              {/* Discovery Call */}
              <div className="bg-slate-900 p-8" data-testid="discovery-call-section">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">
                  Book a Discovery Call
                </p>
                <h3 className="text-2xl font-medium text-white">
                  30 minutes to explore how we can help
                </h3>
                <p className="mt-4 text-slate-300">
                  Use the contact form or email directly to schedule a call. 
                  We'll discuss your challenges and explore how our experience 
                  and the Videra platform can benefit your organisation.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="contact-form-section"
            >
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 p-12 text-center" data-testid="success-message">
                  <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-medium text-slate-900">Thank you!</h3>
                  <p className="mt-4 text-slate-600">
                    Your message has been received. We'll be in touch within 24 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="mt-8 text-slate-900 font-medium hover:text-blue-600 transition-colors"
                    data-testid="send-another-btn"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors"
                        placeholder="Your company"
                        data-testid="input-company"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors"
                        placeholder="+44 XXX XXX XXXX"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors resize-none"
                      placeholder="Tell us about your project challenges and how we can help..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 text-white px-8 py-4 font-medium tracking-wide hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>

                  <p className="text-sm text-slate-500 text-center">
                    We typically respond within 24 hours.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
